import { makeAutoObservable } from 'mobx';
import { fromPromise, IPromiseBasedObservable } from 'mobx-utils';
import extractValue, { EventOrValue } from '../../../store/utils/extractValue';
import { serialize, unserialize } from '../../../store/utils/localstorage';
import Query from '../../../store/utils/query/Query';
import { getFirstMonthDay } from '../../../utils/date';
import Labels from '../../account/store/Labels';
import Bundles from '../../bundles/store/Bundles';
import Products from '../../products/store/Products';
import { PulseKeyDto } from './PulseKey';
import PulseKeys from './PulseKeys';

export default class PulseKeySearch {
  columns = {
    createdAt: false,
    downloadCount: false,
    fulfilledAt: false,
    ipOfDownload: false,
    label: false,
    redemptionIp: false,
    sku: false,
    status: false,
  };

  wasPerformed = false;

  constructor(
    readonly query: Query<PulseKeyDto, PulseKeys>,
    readonly products: Products,
    readonly bundles: Bundles,
    readonly labels: Labels,
    public autoSearch = true,
    public serializable = true,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    if (this.serializable) {
      this.unserialize();
    }

    if (this.autoSearch) {
      this.search();
    }
  }

  get filters(): KeySearchFilters {
    const queryFilters = this.query.filters as any;
    return {
      email: queryFilters.email || '',
      key: queryFilters.key || '',
      label: queryFilters.label || '',
      redeemedAtFrom: queryFilters.redeemedAtFrom || getFirstMonthDay(),
      redeemedAtTo: queryFilters.redeemedAtTo || new Date(),
      sellableId: queryFilters.sellableId || '',
      skipArchived: queryFilters.skipArchived ?? true,
      status: queryFilters.status || '',
    };
  }

  get orderBy(): string {
    return this.query.orderBy;
  }

  get searchPromise(): IPromiseBasedObservable<PulseKeys> {
    if (!this.wasPerformed || !this.query.isExecuted) {
      return fromPromise.resolve(
        new PulseKeys([]),
      ) as IPromiseBasedObservable<PulseKeys>;
    }

    if (!this.query.promise) {
      throw new Error('PulseKeySearch.query.promise is not defined');
    }

    return this.query.promise;
  }

  getSellableDisplayName(sellableId: string): string {
    const bundle = this.bundles.items.find((item) => item.id === sellableId);
    return (
      bundle?.displayName || this.products.getProductDisplayName(sellableId)
    );
  }

  getSellableSku(sellableId: string): string {
    const bundle = this.bundles.items.find((item) => item.id === sellableId);
    return bundle?.sku || this.products.getProductSku(sellableId);
  }

  handleChange(e: EventOrValue) {
    const { name, value } = extractValue(e);

    const [fieldGroup, field] = name.split('.');

    switch (fieldGroup) {
      case 'columns': {
        Object.assign(this.columns, { [field]: Boolean(value) });
        break;
      }

      case 'filters': {
        this.query.setFilter(field, value);
        if (this.autoSearch) {
          this.search();
        }
        break;
      }

      case 'orderBy': {
        this.setOrderBy(value);
        break;
      }

      default:
    }

    if (this.serializable) {
      this.serialize();
    }
  }

  search() {
    this.wasPerformed = true;
    this.query.execute();
  }

  setOrderBy(field: string) {
    this.query.setOrderBy(field);

    if (this.autoSearch) {
      this.search();
    }
  }

  private serialize() {
    serialize('keySearch', {
      columns: this.columns,
      filters: this.query.filters,
    });
  }

  private unserialize() {
    const { columns = {}, filters = {} } =
      unserialize<Record<string, any>>('keySearch', {}) || {};

    if (!this.products?.getById(filters.sellableId)) {
      delete filters.sellableId;
    }

    if (!this.labels?.items.includes(filters.label)) {
      delete filters.label;
    }

    this.query.setFilters({
      email: filters.email,
      key: filters.key,
      label: filters.label,
      sellableId: filters.sellableId,
      skipArchived: filters.skipArchived !== false,
      status: filters.status,
    });

    this.columns = {
      createdAt: Boolean(columns.createdAt),
      downloadCount: Boolean(columns.downloadCount),
      fulfilledAt: Boolean(columns.fulfilledAt),
      ipOfDownload: Boolean(columns.ipOfDownload),
      label: Boolean(columns.label),
      redemptionIp: Boolean(columns.redemptionIp),
      sku: Boolean(columns.sku),
      status: Boolean(columns.status),
    };
  }
}

export interface KeySearchFilters {
  email: string;
  key: string;
  label: string;
  redeemedAtFrom: Date;
  redeemedAtTo: Date;
  sellableId: string;
  skipArchived: boolean;
  status: string;
}
